export * from './functions';

import { getDisplayName, getNewAccessToken, getSpaceUsage } from './functions';
import logger from './logger';

type OneDriveUserData = {
    spaceAvailableMB: number,
    spaceLeft: number,
    spaceTotalMB: number,
    spaceUsedMB: number,
    userEmail: string,
    userName: string
};

/**
 * Resolves Graph API data for a single access token into a structured result.
 *
 * @param {string} token - OneDrive access token.
 * @param {string} clientId - OneDrive application client id.
 * @returns {Promise<?OneDriveUserData>}
 */
function _fetchUserData(token: string, clientId: string): Promise<?OneDriveUserData> {
    return Promise.all([
        getDisplayName(token, clientId),
        getSpaceUsage(token, clientId)
    ]).then(([ userName, space ]) => {
        const allocated = Number(space?.allocated) || 0;
        const used = Number(space?.used) || 0;
        const remaining = Number(space?.remaining);
        const availableBytes = Number.isNaN(remaining)
            ? Math.max(allocated - used, 0)
            : Math.max(remaining, 0);
        const resolvedUserName = space?.ownerName || userName || '';
        const resolvedUserEmail = space?.ownerEmail || '';
        const spaceTotalMB = Math.floor(allocated / 1048576);
        const spaceUsedMB = Math.floor(used / 1048576);
        const spaceAvailableMB = Math.floor(availableBytes / 1048576);

        return {
            spaceAvailableMB,
            spaceLeft: spaceAvailableMB,
            spaceTotalMB,
            spaceUsedMB,
            userEmail: resolvedUserEmail,
            userName: resolvedUserName
        };
    });
}

/**
 * Delays before retrying a Graph request that got no response at all.
 */
const NETWORK_RETRY_DELAYS_MS = [ 1000, 3000 ];

/**
 * Same as {@link _fetchUserData}, retried when the request never got a response. fetch() rejects with a TypeError
 * ("Failed to fetch", Safari's "Load failed") only in that case; Graph error responses are thrown as OneDrive errors
 * and are not retried. One dropped request used to abort the auto-recording storage check at join.
 *
 * @param {string} token - OneDrive access token.
 * @param {string} clientId - OneDrive application client id.
 * @param {number} [attempt] - Retry counter.
 * @returns {Promise<?OneDriveUserData>}
 */
function _fetchUserDataWithRetry(token: string, clientId: string, attempt: number = 0): Promise<?OneDriveUserData> {
    return _fetchUserData(token, clientId).catch(err => {
        if (err instanceof TypeError && attempt < NETWORK_RETRY_DELAYS_MS.length) {
            return new Promise(resolve => setTimeout(resolve, NETWORK_RETRY_DELAYS_MS[attempt]))
                .then(() => _fetchUserDataWithRetry(token, clientId, attempt + 1));
        }

        throw err;
    });
}

/**
 * Loads OneDrive user profile and storage details for a token.
 * If the token is rejected by Microsoft Graph (401/403), and refreshOptions are
 * provided, automatically exchanges the refresh token for a new access token
 * and retries once before failing.
 *
 * @param {string} token - OneDrive access token.
 * @param {string} clientId - OneDrive application client id.
 * @param {Object} [refreshOptions] - Optional token-refresh context.
 * @param {string} refreshOptions.rToken - Refresh token.
 * @param {string} [refreshOptions.strategy] - Auth strategy.
 * @param {Function} [refreshOptions.onRefreshed] - Called with new token result after refresh.
 * @returns {Promise<?OneDriveUserData>}
 */
export function getOneDriveData(
        token: string,
        clientId: string,
        refreshOptions?: Object
): Promise<?OneDriveUserData> {
    return _fetchUserDataWithRetry(token, clientId)
        .catch(err => {
            if (err?.code === 'ONEDRIVE_TOKEN_INVALID' && refreshOptions) {
                const { rToken, strategy, onRefreshed } = refreshOptions;

                return getNewAccessToken(clientId, rToken, strategy)
                    .then(refreshed => {
                        if (typeof onRefreshed === 'function') {
                            onRefreshed(refreshed);
                        }

                        return _fetchUserDataWithRetry(refreshed.token, clientId);
                    });
            }

            logger.error(err);

            return undefined;
        });
}
